import { Box, Grid, Typography } from "@material-ui/core";
import InputSuggestion from "@saleor/alternatedMaterial/components/InputSuggestionForPrioridy";
import { alternatedMaterialUrl } from "@saleor/alternatedMaterial/urls";
import AppHeader from "@saleor/components/AppHeader";
import DialogConfirm from "@saleor/components/DialogConfirm";
import MyTypography from "@saleor/components/MyTypography";
import SingleSelectField from "@saleor/components/SingleSelectField";
import {
  SalesOrganizationsQuery,
  useEditAlternativeMaterialMutation,
  useMaterialOwnLazyQuery,
  useSearchSuggestionDomesticSoldTosLazyQuery
} from "@saleor/graphql";
import useNavigator from "@saleor/hooks/useNavigator";
import { mapEdgesToItems } from "@saleor/utils/maps";
import React, { memo, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import DialogError from "../Common/DialogError";
import DialogSuccess from "../Common/DialogSuccess";
import useAddEditAlternatedMaterial from "../hooks/useAddEditAlternatedMaterial";
import { useAddAltermatedMaterial } from "./EditAlternatedContext";
import MaterialPriorityWrapper from "./PriorityComponent";
import useStyles from "./styles";

export enum ALT_MATERIAL_TYPE {
  MATERIAL = "M",
  GRADE_GRAM = "G"
}

const altMaterialTypes = [
  {
    label: "Material",
    value: ALT_MATERIAL_TYPE.MATERIAL
  },
  {
    label: "Grade/Gram",
    value: ALT_MATERIAL_TYPE.GRADE_GRAM
  }
];

const AddItem: React.FC<{ listOrganization?: SalesOrganizationsQuery }> = ({
  listOrganization
}) => {
  const navigate = useNavigator();
  const [error] = useState<boolean>(false);
  const classes = useStyles();
  const intl = useIntl();
  const [visible, setVisible] = useState<boolean>(false);
  const [visibles, setVisibles] = useState<boolean>(false);
  const [firstLines, setFirstLines] = useState<string | null>(null);
  const [secondLines, setSecondLines] = useState<string | null>(null);
  const [clearOnSaleOrgChanges, setClearOnSaleOrgChanges] = useState(false);
  const datalistOrganization = useMemo(
    () =>
      listOrganization?.salesOrganizations
        .filter(e => e.businessUnit.code === "PP")
        .map(item => ({
          ...item,
          label: `${item.code} - ${item.label}`
        })),
    [listOrganization?.salesOrganizations]
  );
  const {
    formik,
    handleClosePopup,
    openConfirmPopup,
    materialOwnIdError,
    itemDetailData,
    setMaterialOwnIdError
  } = useAddAltermatedMaterial();

  const [
    getSoldToDataSuggestions,
    { data: fetchDataSuggestions, loading: loadingDataSuggestion }
  ] = useSearchSuggestionDomesticSoldTosLazyQuery();

  const [
    getMaterialOwnDataSuggestions,
    {
      data: fetchMaterialOwnDataSuggestions,
      loading: loadingMaterialOwnDataSuggestion
    }
  ] = useMaterialOwnLazyQuery();

  const dataOptions = (
    mapEdgesToItems(fetchDataSuggestions?.searchSuggestionDomesticSoldTos) || []
  ).map(data => ({
    title: `${data.code} - ${data.name}`,
    value: `${data.id}`
  }));

  const materialOwnDataOptions = useMemo(
    () =>
      (
        mapEdgesToItems(fetchMaterialOwnDataSuggestions?.materialAlter) || []
      ).map(data => ({
        title: `${data.code}`,
        value: `${data.id}`
      })),
    [fetchMaterialOwnDataSuggestions?.materialAlter, formik.values.type]
  );
  const handleOnError = () => {
    setVisibles(true);
  };
  const handleOnCloseError = () => {
    setVisibles(false);
  };
  const handleOnComplete = () => {
    setVisible(true);
  };
  const handleBack = () => {
    navigate(alternatedMaterialUrl, {
      replace: true
    });
  };
  const onCloses = () => {
    navigate(alternatedMaterialUrl);
  };
  const getTextError = (field, backupError?: string) => (
    <div className={classes.textError}>
      {formik.errors[field] || backupError}
    </div>
  );

  const [onEditAlterrmatedMaterial] = useEditAlternativeMaterialMutation({
    variables: {
      id: itemDetailData?.alternativeMaterial?.id,
      input: {
        saleOrganizationId: formik.values.saleOrganization,
        soldToId: formik.values.soldTo,
        materialOwnId: formik.values.materialOwn,
        lines: formik.values.priority
          ?.filter(e => !e?.disabled)
          .map((e, index) => ({
            priority: index + 1,
            materialOsId: e.materialOsId,
            dia: e.dia
          }))
      }
    },
    onCompleted(data) {
      const error = data.editAlternativeMaterial.errors;
      const materialOwnError = data.editAlternativeMaterial.errors[0];

      if (data.editAlternativeMaterial.errors.length > 0) {
        const messages = materialOwnError.message.split("\n");

        const newFirstLines = messages.length > 0 ? messages[0] : "";
        setFirstLines(newFirstLines);
        const newSecondLines =
          messages.length > 1 ? messages.slice(1).join("\n") : "";
        setSecondLines(newSecondLines);
        handleOnError();
      }
      if (!error.length) {
        handleOnComplete();
      }
    }
  });

  const handleConfirm = () => {
    formik.submitForm();
    handleClosePopup();
    onEditAlterrmatedMaterial();
  };

  const { saleOrganizationCodeSelected } = useAddEditAlternatedMaterial({
    datalistOrganization,
    saleOrganization: formik.values.saleOrganization
  });

  const handleClearInputOnTypeChanges = event => {
    formik.handleChange(event);
    if (!event) {
      return;
    }
    if (itemDetailData?.alternativeMaterial?.salesOrganization?.id !== event) {
      formik.setFieldValue("materialOwn", "");
      setClearOnSaleOrgChanges(true);
      setTimeout(() => {
        setClearOnSaleOrgChanges(false);
      }, 500);
    }
  };

  return (
    <>
      <AppHeader onBack={handleBack}>
        <MyTypography variant="body3">
          {intl.formatMessage({ defaultMessage: "กลับไป" })}
        </MyTypography>
      </AppHeader>
      <Box className={classes.root}>
        <div className={classes.header}>
          <Typography variant="h6" className={classes.title}>
            {intl.formatMessage({
              defaultMessage: "แก้ไขข้อมูล Master - Alternated Material"
            })}
          </Typography>
        </div>
        <div className={classes.searchBox}>
          <Grid container spacing={6} className={classes.formControl}>
            <Grid item xs={6}>
              <div className={classes.flexs}>
                <MyTypography variant={"subtitle3"}>
                  Sales Organization
                </MyTypography>
                <span className={classes.required}>*</span>
              </div>
              <SingleSelectField
                menuListCustomStyle={{
                  overflowY: "auto"
                }}
                testId="2"
                name={"saleOrganization"}
                id="saleOrganization"
                value={
                  formik.values?.saleOrganization ||
                  itemDetailData?.alternativeMaterial?.salesOrganization.id
                }
                onChange={handleClearInputOnTypeChanges}
                placeholder={"เลือก Sales Organization"}
                error={!!formik.errors.saleOrganization}
                choices={datalistOrganization || []}
                className={classes.selectField}
                InputProps={{
                  inputProps: { "data-testid": "saleOrganization-input" }
                }}
              />
              {getTextError("saleOrganization")}
            </Grid>
            <Grid item xs={6}>
              <div className={classes.flexs}>
                <MyTypography variant={"subtitle3"}>Sold-to Party</MyTypography>
                <span className={classes.required}>*</span>
              </div>
              {itemDetailData?.alternativeMaterial?.soldTo && (
                <InputSuggestion
                  searchField="search"
                  dataOptions={dataOptions}
                  totalCount={dataOptions.length}
                  loadingDataSuggestion={loadingDataSuggestion}
                  getDataSuggestions={getSoldToDataSuggestions}
                  contained
                  defaultValue={{
                    title: `${itemDetailData?.alternativeMaterial.soldTo.code} - ${itemDetailData?.alternativeMaterial.soldTo.name}`,
                    value: `${itemDetailData?.alternativeMaterial.soldTo.id}`
                  }}
                  defaultInputValue={`${itemDetailData?.alternativeMaterial.soldTo.code} - ${itemDetailData?.alternativeMaterial.soldTo.name}`}
                  name={"soldTo"}
                  id="soldTo"
                  testId="soldTo"
                  objectSuggestion
                  placeholder={"ค้นหา Sold to"}
                  error={!!formik.errors.soldTo}
                  handleSelectedSearch={e => {
                    formik.handleChange({
                      target: {
                        name: "soldTo",
                        value: e.value
                      }
                    });
                  }}
                />
              )}
              {getTextError("soldTo")}
            </Grid>
            <Grid item xs={6} className={classes.paddingTop0}>
              <div className={classes.flexs}>
                <MyTypography variant={"subtitle3"}>Type</MyTypography>
                <span className={classes.required}>*</span>
              </div>
              <SingleSelectField
                testId="1"
                menuListCustomStyle={{
                  overflowY: "auto"
                }}
                name={"type"}
                id="type"
                value={formik.values.type}
                onChange={formik.handleChange}
                placeholder={"เลือก Type"}
                choices={(altMaterialTypes || []).filter(
                  e => e.value === formik.values.type
                )}
                className={classes.selectField}
                InputProps={{
                  inputProps: { "data-testid": "type-input" }
                }}
              />
              {getTextError("type")}
            </Grid>
            <Grid item xs={6} className={classes.paddingTop0}>
              <div className={classes.flexs}>
                <MyTypography variant={"subtitle3"}>
                  {formik.values.type === ALT_MATERIAL_TYPE.GRADE_GRAM
                    ? "Grade/Gram Input"
                    : "Material Input"}
                </MyTypography>
                <span className={classes.required}>*</span>
              </div>
              {itemDetailData?.alternativeMaterial &&
                listOrganization?.salesOrganizations && (
                  <InputSuggestion
                    searchField={
                      formik.values.type === ALT_MATERIAL_TYPE.MATERIAL
                        ? "materialCode"
                        : "gradeGramCode"
                    }
                    dataOptions={materialOwnDataOptions}
                    totalCount={materialOwnDataOptions.length}
                    loadingDataSuggestion={loadingMaterialOwnDataSuggestion}
                    getDataSuggestions={getMaterialOwnDataSuggestions}
                    contained
                    isCheckingRole
                    defaultInputValue={`${itemDetailData?.alternativeMaterial?.materialOwn?.code}`}
                    defaultValue={{
                      title: `${itemDetailData?.alternativeMaterial?.materialOwn.code}`,
                      value: `${itemDetailData?.alternativeMaterial?.materialOwn.id}`
                    }}
                    objectSuggestion
                    name={"materialOwn"}
                    id="materialOwn"
                    testId="materialOwn"
                    placeholder={
                      formik.values.type === ALT_MATERIAL_TYPE.GRADE_GRAM
                        ? "ระบุ Grade/Gram Own"
                        : "ค้นหา Material Own"
                    }
                    error={!!formik.errors.materialOwn || !!materialOwnIdError}
                    handleSelectedSearch={e => {
                      setMaterialOwnIdError("");
                      formik.handleChange({
                        target: {
                          name: "materialOwn",
                          value: e.value
                        }
                      });
                    }}
                    saleOrganization={saleOrganizationCodeSelected}
                    isClean={clearOnSaleOrgChanges}
                    resetInputValueOnBlur
                  />
                )}
              <div className={classes.textError}>
                {formik.errors.materialOwn
                  ? "โปรดระบุข้อมูล"
                  : getTextError("materialOwn", materialOwnIdError)}
              </div>
              {getTextError("", materialOwnIdError)}
            </Grid>
            {formik.values.type && (
              <Grid item xs={6} md={12} className={classes.paddingTop0}>
                <MaterialPriorityWrapper
                  type={formik.values.type}
                  saleOrganization={saleOrganizationCodeSelected}
                  isSaleOrgChanges={clearOnSaleOrgChanges}
                />
              </Grid>
            )}
          </Grid>
          {error ? (
            <MyTypography className={classes.errorMessage} variant="caption">
              {intl.formatMessage({
                defaultMessage: "กรุณากรอกข้อมูลที่ต้องการค้นหา"
              })}
            </MyTypography>
          ) : null}
        </div>
      </Box>
      <DialogConfirm
        onCancel={handleClosePopup}
        onConfirm={handleConfirm}
        open={openConfirmPopup}
        title={
          <span style={{ color: "#009FB8" }}>
            <span style={{ fontWeight: "bold" }}>บันทึกข้อมูล </span>
            <span style={{ fontWeight: 400 }}>
              Master - Alternated Material
            </span>
          </span>
        }
        content={intl.formatMessage({
          defaultMessage: `กรุณากด “ยืนยัน” เพื่อบันทึกข้อมูล Master - Alternated Material หรือกด “กลับไป” เพื่อปิดหน้าต่างนี้`
        })}
        cancelText={intl.formatMessage({ defaultMessage: "กลับไป" })}
      />
      <DialogSuccess
        visible={visible}
        onClose={onCloses}
        title={"บันทึกข้อมูล Master - Alternated Material "}
      />
      <DialogError
        visible={visibles}
        onClose={handleOnCloseError}
        title={"ไม่สามารถทำรายการได้"}
        firstLine={firstLines}
        secondLine={secondLines}
      />
    </>
  );
};

export default memo(AddItem);
